import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikErrors, useFormik } from 'formik';
import React from 'react';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelSelectPalette from '../../components/muiLegacy/Select/VoxelSelectPalette';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../components/voxel/Buttons/VoxelButton';
import VoxelInput from '../../components/voxel/Forms/VoxelInput';
import VoxelText from '../../components/voxel/Text/VoxelText';
import { AnalysisColorPaletteGq } from '../../graphql/projects/models/AnalysisColorPaletteGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { CurrentTeamGq } from '../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import { usersApiService } from '../../graphql/users/usersApiService';
import VoxelToggleButton from '../../components/voxel/Tabs/VoxelToggleButton';
import VoxelSwitch from '../../components/voxel/Forms/VoxelSwitch';

export interface TeamSettingsPageProps {
  user: CurrentUserGq;
  team: CurrentTeamGq;
  reload: () => Promise<unknown>;
}

export interface TeamSettingsFormState {
  teamName: string;
  betaTester: boolean;
  palette?: AnalysisColorPaletteGq;
}

export const TeamSettingsPage: React.FC<TeamSettingsPageProps> = ({ user, team, reload }) => {
  // Palette data
  const palettesQuery = projectsApiService.queries.analysis.useGetAnalysisColorPalettes();

  // Team settings form
  const handleUpdateTeamOptions = usersApiService.mutations.useEditTeamOptions();
  const formikProps = useFormik<TeamSettingsFormState>({
    initialValues: {
      teamName: team.name,
      betaTester: team.betaTester,
      palette: palettesQuery?.data?.find((p) => p.id === team.defaultPaletteId),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: FormikErrors<TeamSettingsFormState> = {};
      if (!values.teamName.length) {
        errors.teamName = `Field cannot be empty`;
      }
      return errors;
    },
    onSubmit: (values, actions) =>
      handleUpdateTeamOptions({
        teamName: values.teamName,
        defaultPaletteId: values.palette?.id > 0 ? values.palette?.id : null,
        betaTester: !!values.betaTester,
      })
        .catch((reason: any) => console.error(reason))
        .then(() => reload()),
  });

  if (!user || !team) {
    return <LoadingScreen />;
  }

  return (
    <form
      className="flex flex-col gap-12"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {!user.team.accessUserCanEditBasicSettings && (
        <VoxelAlert severity="error" size="sm" iconSize="lg">
          You do not have permission to edit team settings
        </VoxelAlert>
      )}
      <div className="flex flex-col gap-4">
        <VoxelText variant="title2">General</VoxelText>
        <div>
          <VoxelInput
            label="Team name*"
            labelPosition="top"
            value={formikProps.values.teamName}
            onChange={(event) => formikProps.setFieldValue('teamName', event.target.value)}
            deleteValue={() => formikProps.setFieldValue('teamName', '')}
            helperText={formikProps.errors?.teamName}
            showHelperText={formikProps.errors.teamName?.length > 0}
            disabled={formikProps.isSubmitting || !user.team.accessUserCanEditBasicSettings}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div>
          <VoxelText variant="title2">Project defaults</VoxelText>
          <VoxelText variant="bread-normal" paddingTop>
            These settings are defaults for your new projects, unless overridden by a user's
            personal settings.
          </VoxelText>
        </div>
        <div className="w-64">
          <VoxelSelectPalette
            selectedPalette={formikProps.values?.palette}
            setSelectedPalette={(option) => formikProps.setFieldValue('palette', option)}
            disabled={
              formikProps.isSubmitting ||
              !user.team.accessUserCanEditBasicSettings ||
              !palettesQuery?.data?.length
            }
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <VoxelText variant="title2">Experimental features</VoxelText>
        </div>
        <VoxelSwitch
          onLabel="On"
          value={formikProps.values.betaTester}
          onChange={(checked) =>
            formikProps.setFormikState((prev) => ({
              ...prev,
              values: { ...prev.values, betaTester: checked },
            }))
          }
        />
      </div>

      <div>
        <VoxelButton
          title={'Save changes'}
          color="primary"
          endIcon={<FontAwesomeIcon icon={faSave} size="lg" />}
          onClick={formikProps.submitForm}
          disabled={
            formikProps.isSubmitting ||
            !user.team.accessUserCanEditBasicSettings ||
            !formikProps.dirty ||
            !formikProps.isValid
          }
        />
      </div>
    </form>
  );
};

export default TeamSettingsPage;
